import React from "react";

import {
  AddCircleRounded,
  AddRounded,
  CloseRounded,
  DeleteOutlineRounded,
  DeleteRounded,
  DragIndicatorRounded,
  EditRounded,
  SaveRounded,
} from "@mui/icons-material";
import { Box, DialogContent, Divider } from "@mui/material";
import { nanoid } from "nanoid";

import useModal from "../../../../config/context/modalContext";
import { CafeteriaService } from "../../../../config/services/CafeteriaService";

import SwitchMain from "../../../../components/InputMain/SwitchMain";
import TextMain from "../../../../components/InputMain/TextMain";
import MainBorderButton from "../../../../components/MainBorderButton";
import { WideDialog } from "../../../../components/MainModalSimple/styles";
import MainSolidButton from "../../../../components/MainSolidButton";
import { ModalTransition } from "../../../../components/ModalTransition";
import PageHeader from "../../../../components/PageHeader";

import { arrayMove, List } from "react-movable";
import SimpleBase64 from "../../../../components/InputImage/SimpleBase64";
import SelectMain from "../../../../components/InputMain/SelectMain";
import MainCollapseBox from "../../../../components/MainCollapseBox";
import MainText from "../../../../components/MainText";
import { GreyPallet } from "../../../../config/styles";
import { ModalBaseType } from "../../../../types/common";
import { CafeteriaFoodItemType, UserUploadedFileType } from "../../../../types/database";

type ModalWriteItemProps = ModalBaseType & {
  cafeteriaId: string;
  menu: CafeteriaFoodItemType[];
  foodItem?: CafeteriaFoodItemType;
};

export default function ModalWriteItem({
  onClose,
  foodItem,
  menu,
  cafeteriaId,
}: ModalWriteItemProps) {
  const { pushAlert } = useModal();

  const CafeteriaUpdate = CafeteriaService.Update();

  const [open, setOpen] = React.useState(true);

  const itemId = foodItem ? foodItem.id : nanoid(8);
  const [nameField, setNameField] = React.useState(foodItem?.name || "");
  const [descriptionField, setDescriptionField] = React.useState(foodItem?.description || "");
  const [priceField, setPriceField] = React.useState(String(foodItem?.price) || "");
  const [availableField, setAvailableField] = React.useState(foodItem?.available || false);

  const [hideAllCategories, setHideAllCategories] = React.useState<boolean | undefined>(undefined);

  const [imageField, setImageField] = React.useState<UserUploadedFileType[]>(
    foodItem?.image ? [foodItem?.image] : []
  );

  const [deleteLoading, setDeleteLoading] = React.useState(false);

  const [categoryList, setCategoryList] = React.useState<any[]>([]);
  const [changeOrder, setChangeOrder] = React.useState(false);

  const handleWrite = async (isDelete?: boolean) => {
    if (isDelete) setDeleteLoading(true);

    const currentItem = {
      id: foodItem ? foodItem.id : itemId,
      name: nameField,
      price: parseFloat(priceField.replace(",", ".")).toFixed(2),
      available: availableField,
      description: descriptionField,
      image: imageField.length ? imageField[0] : false,
      order: foodItem ? foodItem.order : menu.length,
    };

    const updatedMenu = foodItem
      ? isDelete
        ? menu.filter((item) => item.id !== foodItem.id)
        : menu.map((item) => (item.id === foodItem.id ? currentItem : item))
      : [...menu, currentItem];

    await CafeteriaUpdate.runRequest(cafeteriaId, { menu: updatedMenu })
      .then(() => {
        pushAlert({ type: "success", message: "Menu atualizado." });
        handleClose(true);
      })
      .catch((err) =>
        pushAlert({ type: "error", message: "Erro ao atualizar menu. " + err.message })
      );
    setDeleteLoading(false);
  };

  const handleClose = (reload: boolean = false) => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose(reload);
    }, 200);
  };

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={foodItem ? "Editar Item" : "Adicionar Item"}
          rightIcon={<CloseRounded />}
          onRightAction={() => handleClose()}
        />

        <Box>
          <Box mt={4} position={"relative"}>
            <Box mb={2}>
              <SimpleBase64
                imageHeight={120}
                fileList={imageField}
                setFileList={setImageField}
                isImage
              />
            </Box>

            <TextMain name="Nome" value={nameField} onChange={setNameField} textColor={"regular"} />

            <TextMain
              name="Descrição"
              value={descriptionField}
              onChange={setDescriptionField}
              textColor={"regular"}
            />

            <TextMain
              name="Preço Base"
              value={priceField}
              onChange={setPriceField}
              textColor={"regular"}
              inputMode="decimal"
              inputType="number"
            />

            {/* Categoria */}

            <List
              lockVertically
              values={categoryList}
              onChange={({ oldIndex, newIndex }) => {
                setCategoryList(arrayMove(categoryList, oldIndex, newIndex));
              }}
              renderList={({ children, props }) => <div {...props}>{children}</div>}
              renderItem={({ value, props, isDragged, index }) => (
                <Box {...props} key={value.id} mb={1} zIndex={10000}>
                  <MainCollapseBox
                    title={
                      changeOrder ? (
                        <Box data-movable-handle display={"flex"} color={GreyPallet.Regular}>
                          <DragIndicatorRounded /> <MainText color="regular">Proteína</MainText>
                        </Box>
                      ) : (
                        <MainText color="regular">{(index || 0) + 1}° - Proteína</MainText>
                      )
                    }
                    disableToggle={hideAllCategories}
                    show={hideAllCategories ? false : undefined}
                  >
                    <TextMain
                      name="Nome da Categoria"
                      value={""}
                      onChange={() => {}}
                      textColor={"regular"}
                    />
                    <TextMain
                      name="Valor Mínimo de Itens"
                      value={""}
                      onChange={() => {}}
                      textColor={"regular"}
                    />
                    <TextMain
                      name="Valor Máximo de Itens"
                      value={""}
                      onChange={() => {}}
                      textColor={"regular"}
                    />
                    <Box mb={1}>
                      <SelectMain
                        name="Tipo da Categoria"
                        value={""}
                        options={[
                          { name: "Seleção Única", value: "1" },
                          { name: "Seleção Múltipla", value: "2" },
                        ]}
                        onChange={(value) => {}}
                      />
                    </Box>

                    <Box mb={2}>
                      <MainText color="regular" size="small">
                        Na Seleção Única, cada um dos itens da categoria só podem ser escolhidos uma
                        vez. Ex.: Caso esteja vendendo uma pizza, o usuário só pode escolher "Borda
                        de Catupiri" ou "Borda de Cheedar" ou "Sem Borda"
                      </MainText>
                    </Box>

                    <Box mb={2}>
                      <MainText color="regular" size="small">
                        Na Seleção Múltipla, cada um dos itens da categoria podem ser escolhidos
                        várias vezes. Ex.: Caso esteja vendendo açaí, o usuário pode escolher 5
                        recheios, ele tanto pode escolher 5 diferentes como 5 iguais.
                      </MainText>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box mb={2}>
                      <MainText color="regular" size="small">
                        Nenhum item adicionado nessa categoria
                      </MainText>
                    </Box>

                    <Box bgcolor={GreyPallet.LightGhost} borderRadius={"10px"} px={2} py={1} mb={2}>
                      <Box mb={2}>
                        <SimpleBase64
                          imageHeight={120}
                          fileList={imageField}
                          setFileList={setImageField}
                          isImage
                        />
                      </Box>

                      <TextMain
                        name="Nome do Item"
                        value={""}
                        onChange={() => {}}
                        textColor={"regular"}
                      />

                      <TextMain
                        name="Descrição do Item"
                        value={""}
                        onChange={() => {}}
                        textColor={"regular"}
                      />

                      <TextMain
                        name="Valor do Item"
                        value={""}
                        onChange={() => {}}
                        textColor={"regular"}
                      />

                      <SwitchMain
                        name="Disponível"
                        checked={availableField}
                        onChange={setAvailableField}
                      />

                      <Box mt={2} mb={1}>
                        <MainBorderButton
                          text={"Remover Item"}
                          icon={<DeleteRounded />}
                          onClick={() => {}}
                        />
                      </Box>
                    </Box>

                    <Box mb={2}>
                      <MainSolidButton
                        text={"Adicionar Item"}
                        icon={<AddRounded />}
                        onClick={() => {}}
                      />
                    </Box>

                    <Box mt={2} mb={1}>
                      <MainBorderButton
                        text={"Remover Categoria"}
                        icon={<DeleteRounded />}
                        onClick={() => {}}
                      />
                    </Box>
                  </MainCollapseBox>
                </Box>
              )}
            />

            <Box mb={2}>
              <MainBorderButton
                icon={changeOrder ? <SaveRounded /> : <EditRounded />}
                text={changeOrder ? "Salvar Ordem" : "Editar Ordem"}
                onClick={() => {
                  changeOrder ? setHideAllCategories(undefined) : setHideAllCategories(true);
                  setChangeOrder(!changeOrder);
                }}
              />
            </Box>

            <Box mb={2}>
              <MainSolidButton
                text={"Adicionar Categoria"}
                icon={<AddRounded />}
                onClick={() => setCategoryList([...categoryList, categoryList.length + 1])}
              />
            </Box>

            <SwitchMain
              name="Disponível"
              small={"Quando ativado o item aparece para os clientes mas eles não podem adicionar."}
              checked={availableField}
              onChange={setAvailableField}
            />

            <SwitchMain
              name="Visível"
              small={"Quando ativado, o item aparece para o cliente adicionar."}
              checked={availableField}
              onChange={setAvailableField}
            />

            {/* End Categoria */}
          </Box>

          {!!foodItem && (
            <MainBorderButton
              color="danger"
              text="Deletar"
              loading={deleteLoading}
              icon={<DeleteOutlineRounded />}
              onClick={() =>
                pushAlert({
                  isConfirm: true,
                  type: "delete",
                  onConfirm: () => handleWrite(true),
                  message: "Você deseja realmente remover esse item?",
                })
              }
            />
          )}

          <Box mt={2} mb={1}>
            <MainSolidButton
              loading={CafeteriaUpdate.isLoading && !deleteLoading}
              text={foodItem ? "Salvar" : "Adicionar"}
              icon={foodItem ? <SaveRounded /> : <AddCircleRounded />}
              onClick={() => handleWrite()}
            />
          </Box>
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
